/*
 * Copyright (c) CIRROM 2025.
 */

import React from "react";
import {
  Grid,
  CircularProgress,
  Box,
  Typography,
  CardHeader,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { UserActivitySummary as UserActivitySummaryType } from "../../api/dashboards";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import { NUMBER_FORMATTER } from "../utils/utils";
import styled from "@emotion/styled";

const TableContainer = styled.div`
  height: 400px;
  overflow-y: auto;
`;

interface UserActivitySummaryProps {
  userActivitySummary: UserActivitySummaryType[];
  isLoading: boolean;
}

export const UserActivitySummary: React.FC<UserActivitySummaryProps> = ({
  userActivitySummary,
  isLoading,
}) => {
  const calculateApprovalRate = (approved: number, total: number) => {
    if (total === 0) return "0%";
    return `${Math.round((approved / total) * 100)}%`;
  };

  const calculateRejectionRate = (rejected: number, total: number) => {
    if (total === 0) return "0%";
    return `${Math.round((rejected / total) * 100)}%`;
  };

  return (
    <>
      <Grid justifyContent="space-between" container spacing={6}>
        <Grid>
          <Typography variant="h4" gutterBottom>
            User Activity Summary
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={6}>
        <Grid
          size={{
            xs: 12,
            lg: 12,
          }}
        >
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <MiraCard>
              <CardHeader title="User Performance Overview" />
              <MiraCardContent>
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography
                            variant="caption"
                            style={{ fontWeight: "bold" }}
                          >
                            User
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="caption"
                            style={{ fontWeight: "bold" }}
                          >
                            Total Changes
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="caption"
                            style={{ fontWeight: "bold" }}
                          >
                            Approved
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="caption"
                            style={{ fontWeight: "bold" }}
                          >
                            Rejected
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="caption"
                            style={{ fontWeight: "bold" }}
                          >
                            Duplicates Processed
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="caption"
                            style={{ fontWeight: "bold" }}
                          >
                            Approval Rate
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userActivitySummary.map((user) => (
                        <TableRow key={user.user_id}>
                          <TableCell component="th" scope="row">
                            <Typography
                              variant="body2"
                              style={{ fontWeight: "500" }}
                            >
                              {user.user_name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {NUMBER_FORMATTER.format(user.total_changes)}
                          </TableCell>
                          <TableCell align="right">
                            {NUMBER_FORMATTER.format(user.approved_changes)}
                          </TableCell>
                          <TableCell align="right">
                            {NUMBER_FORMATTER.format(user.rejected_changes)}
                          </TableCell>

                          <TableCell align="right">
                            {NUMBER_FORMATTER.format(
                              user.duplicates_processed ?? 0
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              style={{
                                color:
                                  user.approved_changes / user.total_changes >
                                  0.8
                                    ? "green"
                                    : user.approved_changes /
                                        user.total_changes >
                                      0.6
                                    ? "orange"
                                    : "red",
                                fontWeight: "500",
                              }}
                            >
                              {calculateApprovalRate(
                                user.approved_changes,
                                user.total_changes
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </MiraCardContent>
            </MiraCard>
          )}
        </Grid>
      </Grid>
    </>
  );
};
